import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, MapPin, Users, Trophy, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUserGroupInfo } from '@/hooks/useUserGroupInfo';
import { useTournamentSchedule } from '@/hooks/useTournamentSchedule';
import { useLeagueMatches } from '@/hooks/useLeagueMatches';
import { useTennisEncounterForPreview } from '@/hooks/useTennisEncounterForPreview';
import { useTennisChallengeEncounters } from '@/hooks/useTennisChallengeEncounters';
import { useUserRegistration } from '@/hooks/useUserRegistration';
import { parseLocalDate } from '@/utils/dateUtils';
import TennisChallengeCard from '@/components/league/TennisChallengeCard';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatScheduleDate } from '@/utils/groupScheduleParser';
import type { MyEvent } from '@/hooks/useMyEvents';

interface UpcomingEventPreviewProps {
  events: MyEvent[];
  onEventClick: (event: MyEvent) => void;
}

const formatTo12h = (time: string): string => {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m || '00'} ${ampm}`;
};

/** Single event card content – extracted to keep the carousel wrapper clean */
const EventSlide = ({ event, onEventClick }: { event: MyEvent; onEventClick: (e: MyEvent) => void }) => {
  const [countdown, setCountdown] = useState('');
  const [countdownLabel, setCountdownLabel] = useState('');
  const [matchCarouselApi, setMatchCarouselApi] = useState<CarouselApi>();
  const [matchCurrentSlide, setMatchCurrentSlide] = useState(0);
  const { t, language } = useLanguage();

  const isTournament = !event.isLeague;
  const { groupInfo, loading: groupLoading } = useUserGroupInfo(isTournament ? event.id : '');
  const { eliminationMatches, loading: scheduleLoading } = useTournamentSchedule(
    isTournament ? event.id : '', event.category, null
  );
  const { registration } = useUserRegistration(isTournament ? event.id : '');
  const { userMatchForEncounter, userTeam, opponentTeam, isLoading: leagueLoading, matchesByEncounter, setSelectedEncounter } = useLeagueMatches(
    event.isLeague ? event.id : ''
  );
  const { encounterInfo, isTennis } = useTennisEncounterForPreview(
    event.id, event.sport || '', !!event.isLeague, event.startDate
  );
  const { userEncountersByWeek } = useTennisChallengeEncounters(
    isTennis ? event.id : ''
  );

  // Get the tennis challenge for the current encounter
  const tennisChallenge = isTennis && encounterInfo 
    ? userEncountersByWeek[encounterInfo.encounterNumber] 
    : null;
  useEffect(() => {
    if (!event.isLeague || !matchesByEncounter || Object.keys(matchesByEncounter).length === 0) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const encounters = Object.entries(matchesByEncounter)
      .map(([enc, matches]) => ({
        encounter: Number(enc),
        date: matches.find(m => m.scheduled_date)?.scheduled_date
      }))
      .filter(e => e.date)
      .sort((a, b) => a.encounter - b.encounter);

    if (encounters.length === 0) return;

    const next = encounters.find(e => {
      const d = parseLocalDate(e.date!);
      return d >= today;
    });

    const targetEncounter = next?.encounter ?? encounters[encounters.length - 1].encounter;
    setSelectedEncounter(targetEncounter);
  }, [event.isLeague, matchesByEncounter, setSelectedEncounter]);


  const dateLocaleStr = language === 'es' ? 'es-ES' : 'en-US';

  // Filter user's matches from groupInfo
  const userMatches = useMemo(() => {
    if (!groupInfo || !groupInfo.matches.length) return [];
    const userParticipant = groupInfo.participants.find(p => p.isUser);
    if (!userParticipant) return groupInfo.matches;
    return groupInfo.matches.filter(m => m.participants.includes(userParticipant.letter));
  }, [groupInfo]);

  // Build teamName from registration data (same logic as TournamentEliminationMatchesView)
  const regPlayer1 = registration?.player1_name || null;
  const regPlayer2 = registration?.player2_name || null;
  const regTeamName = regPlayer1 && regPlayer2
    ? `${regPlayer1}/${regPlayer2}`
    : regPlayer1 || '';

  const isUserMatchSchedule = useCallback((match: any) => {
    if (!match.participants || !Array.isArray(match.participants) || !regTeamName) return false;
    return match.participants.some((p: string) => {
      return p === regTeamName || p.includes(regPlayer1 || '___') || (regPlayer2 && p.includes(regPlayer2));
    });
  }, [regTeamName, regPlayer1, regPlayer2]);

  // Fallback: derive matches from tournament_schedules (groupBlocks) when useUserGroupInfo has no data
  const scheduleMatches = useMemo(() => {
    if (userMatches.length > 0 || !eliminationMatches.length) return [];
    // Filter to group-phase matches only
    const groupMatches = eliminationMatches.filter(m => m.phase && m.phase.toLowerCase().startsWith('grupo'));
    if (!groupMatches.length) return [];
    // Filter to user's matches using registration names (same as TournamentEliminationMatchesView)
    return groupMatches.filter(isUserMatchSchedule);
  }, [userMatches, eliminationMatches, isUserMatchSchedule]);

  // Effective matches to display (prefer groupInfo, fallback to schedule)
  const effectiveMatches = userMatches.length > 0 ? userMatches : scheduleMatches;
  const isUsingScheduleFallback = userMatches.length === 0 && scheduleMatches.length > 0;

  // Derive the first match time from user's filtered matches
  const firstMatchTime = useMemo(() => {
    if (isTournament && effectiveMatches.length > 0) {
      const first = effectiveMatches[0] as any;
      return first.time || first.startTime || null;
    }
    // Para Liga Pádel: usar la hora oficial de la liga (event.time), no el slot individual del jugador
    const isPadelLeague = event.isLeague && (event.sport?.toLowerCase() === 'pádel' || event.sport?.toLowerCase() === 'padel');
    if (isPadelLeague) {
      return null;
    }
    if (event.isLeague && userMatchForEncounter?.scheduled_time) {
      return userMatchForEncounter.scheduled_time;
    }
    return null;
  }, [isTournament, effectiveMatches, event.isLeague, event.sport, userMatchForEncounter]);

  const tournamentLoading = groupLoading || (userMatches.length === 0 && scheduleLoading);

  // For tournaments: derive the user's actual first/next match date instead of the tournament start date
  const userFirstMatchDate = useMemo(() => {
    if (!isTournament) return null;
    const dates = effectiveMatches
      .map((m: any) => m.scheduled_date || m.date)
      .filter(Boolean) as string[];
    if (dates.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sorted = [...dates].sort((a, b) => parseLocalDate(a).getTime() - parseLocalDate(b).getTime());
      const futureOrToday = sorted.find(d => parseLocalDate(d) >= today);
      return futureOrToday ?? sorted[0];
    }
    // Fallback: top-level date from the user's group block in the tournament schedule
    if (groupInfo?.date) return groupInfo.date;
    return null;
  }, [isTournament, effectiveMatches, groupInfo]);

  const formattedDate = useMemo(() => {
    if (isTennis && encounterInfo) {
      return encounterInfo.dateRangeLabel;
    }
    // For tournaments, ONLY use the match-schedule date. If unavailable, hide the date line.
    if (isTournament) {
      if (!userFirstMatchDate) return '';
      const date = parseLocalDate(userFirstMatchDate);
      if (!isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(userFirstMatchDate)) {
        return date.toLocaleDateString(dateLocaleStr, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        });
      }
      // Fallback: groupCalendar.day is a human string like "Jueves 30 de octubre" — show as-is
      const raw = String(userFirstMatchDate).trim();
      return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
    }
    const dateStr = (event.isLeague && userMatchForEncounter?.scheduled_date)
      ? userMatchForEncounter.scheduled_date
      : event.startDate;
    const date = parseLocalDate(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(dateLocaleStr, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }, [event.startDate, event.isLeague, userMatchForEncounter, isTennis, encounterInfo, dateLocaleStr, isTournament, userFirstMatchDate]);

  // Countdown timer
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      let eventDate: Date;

      // For Tennis leagues, use the encounter week start date
      if (isTennis && encounterInfo) {
        eventDate = new Date(encounterInfo.weekStartDate);
      } else {
        // For tournaments, only use the user's match-schedule date. If absent, skip countdown.
        if (isTournament) {
          if (!userFirstMatchDate) {
            setCountdown('');
            setCountdownLabel('');
            return;
          }
          eventDate = parseLocalDate(userFirstMatchDate);
        } else {
          const dateStr = (event.isLeague && userMatchForEncounter?.scheduled_date)
            ? userMatchForEncounter.scheduled_date
            : event.startDate;
          eventDate = parseLocalDate(dateStr);
        }
      }

      if (isNaN(eventDate.getTime())) {
        setCountdown('');
        setCountdownLabel('');
        return;
      }

      if (!isTennis) {
        const timeToUse = firstMatchTime || event.time;
        if (timeToUse) {
          const [h, m] = timeToUse.split(':');
          eventDate.setHours(parseInt(h, 10), parseInt(m || '0', 10), 0, 0);
        }
      }

      const diff = eventDate.getTime() - now.getTime();
      
      if (diff <= 0) {
        setCountdown(isTennis ? t('dates.thisWeek') : t('dates.today'));
        setCountdownLabel('');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setCountdown(`${days}`);
        setCountdownLabel(days === 1 ? t('dates.day') : t('dates.days'));
      } else if (hours > 0) {
        setCountdown(`${hours}`);
        setCountdownLabel(hours === 1 ? t('dates.hour') : t('dates.hours'));
      } else {
        setCountdown(`${minutes}`);
        setCountdownLabel(t('dates.min'));
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [event.startDate, event.time, event.isLeague, userMatchForEncounter, isTennis, encounterInfo, t, firstMatchTime, isTournament, userFirstMatchDate]);

  // Match carousel effect
  useEffect(() => {
    if (!matchCarouselApi) return;
    setMatchCurrentSlide(matchCarouselApi.selectedScrollSnap());
    const onSelect = () => setMatchCurrentSlide(matchCarouselApi.selectedScrollSnap());
    matchCarouselApi.on('select', onSelect);
    return () => { matchCarouselApi.off('select', onSelect); };
  }, [matchCarouselApi]);

  // Helper to resolve participant letter to name
  const getParticipantName = (letter: string) => {
    const p = groupInfo?.participants.find(pp => pp.letter === letter);
    if (!p) return letter;
    return p.player2 ? `${p.player1} / ${p.player2}` : p.player1;
  };

  const isUserLetter = (letter: string) => {
    return groupInfo?.participants.find(p => p.letter === letter)?.isUser ?? false;
  };

  return (
    <Card 
      className="overflow-hidden border-0 shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300"
      onClick={() => onEventClick(event)}
    >
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-[#1b3521] via-[#31633f] to-[#1b3521] p-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" 
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(231,209,182,0.3) 0%, transparent 50%)' }} 
        />
        
        <div className="relative flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-[#e7d1b6]/20 text-[#e7d1b6] border-[#e7d1b6]/30 text-[10px] md:text-xs font-medium">
                {event.isLeague ? `🏆 ${t('events.league')}` : `🎾 ${t('events.tournament')}`}
              </Badge>
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px] md:text-xs">
                {t('events.upcoming')}
              </Badge>
              {isTennis && encounterInfo && (
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px] md:text-xs font-medium">
                  {t('events.encounter')} {encounterInfo.encounterNumber}
                </Badge>
              )}
            </div>

            <h3 className="text-base md:text-lg font-bold text-white truncate">
              {event.title}
            </h3>
            
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5 text-[#e7d1b6]/80 text-xs md:text-sm">
                <CalendarDays className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{formattedDate}</span>
              </div>
              {(() => {
                const displayTime = (isTournament && firstMatchTime) || event.time;
                return displayTime ? (
                  <div className="flex items-center gap-1.5 text-[#e7d1b6]/80 text-xs md:text-sm">
                    <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{formatTo12h(displayTime)}</span>
                  </div>
                ) : null;
              })()}
              <div className="flex items-center gap-1.5 text-[#e7d1b6]/80 text-xs md:text-sm">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{event.location}</span>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              {event.category && (
                <span className="text-[10px] md:text-xs bg-[#31633f]/50 text-[#e7d1b6]/90 px-2 py-0.5 rounded-full">
                  {event.category}
                </span>
              )}
              {event.partner && (
                <span className="text-[10px] md:text-xs text-[#e7d1b6]/70 flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {event.partner}
                </span>
              )}
            </div>
          </div>

          {countdown && (
            <div className="flex-shrink-0 bg-[#e7d1b6]/10 border border-[#e7d1b6]/20 rounded-lg px-2.5 py-1.5 text-center">
              <div className="text-sm md:text-base font-bold text-[#e7d1b6] leading-tight">{countdown}</div>
              <div className="text-[9px] md:text-[10px] text-[#e7d1b6]/70 uppercase tracking-wide">{countdownLabel}</div>
            </div>
          )}
        </div>
      </div>

      {/* Content area */}
      <CardContent className="p-0">
        {isTournament && !tournamentLoading && effectiveMatches.length > 0 && (
          <div className="p-3 md:p-4" onClick={(e) => e.stopPropagation()}>
            {/* Group header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {groupInfo?.groupName && !isUsingScheduleFallback && (
                  <Badge variant="default" className="text-[10px]">
                    {groupInfo.groupName}
                  </Badge>
                )}
                {isUsingScheduleFallback && effectiveMatches[0] && (
                  <Badge variant="default" className="text-[10px]">
                    {(effectiveMatches[0] as any).phase || ''}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {effectiveMatches.length} {effectiveMatches.length === 1 
                    ? (language === 'es' ? 'partido' : 'match') 
                    : (language === 'es' ? 'partidos' : 'matches')}
                </span>
              </div>
              {effectiveMatches.length > 1 && (
                <Badge variant="outline" className="text-[10px]">
                  {matchCurrentSlide + 1}/{effectiveMatches.length}
                </Badge>
              )}
            </div>

            {effectiveMatches.length === 1 ? (
              /* Single match — no carousel needed */
              <div className="bg-primary/5 rounded-lg border border-primary/10 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <p className={`text-xs md:text-sm font-semibold ${
                      isUsingScheduleFallback 
                        ? isUserMatchSchedule({ participants: [(effectiveMatches[0] as any).participants?.[0]] }) ? 'text-primary' : 'text-foreground'
                        : isUserLetter(effectiveMatches[0].participants[0]) ? 'text-primary' : 'text-foreground'
                    }`}>
                      {isUsingScheduleFallback 
                        ? (effectiveMatches[0] as any).participants?.[0] || ''
                        : getParticipantName(effectiveMatches[0].participants[0])}
                    </p>
                  </div>
                  <span className="mx-2 text-xs font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5">VS</span>
                  <div className="text-center flex-1">
                    <p className={`text-xs md:text-sm font-semibold ${
                      isUsingScheduleFallback 
                        ? isUserMatchSchedule({ participants: [(effectiveMatches[0] as any).participants?.[1]] }) ? 'text-primary' : 'text-foreground'
                        : isUserLetter(effectiveMatches[0].participants[1]) ? 'text-primary' : 'text-foreground'
                    }`}>
                      {isUsingScheduleFallback 
                        ? (effectiveMatches[0] as any).participants?.[1] || ''
                        : getParticipantName(effectiveMatches[0].participants[1])}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-3 mt-2 text-[10px] md:text-xs text-muted-foreground">
                  {((effectiveMatches[0] as any).startTime || (effectiveMatches[0] as any).time) && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatTo12h(isUsingScheduleFallback ? (effectiveMatches[0] as any).startTime : (effectiveMatches[0] as any).time)}
                    </span>
                  )}
                  {((effectiveMatches[0] as any).court) && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {t('events.court')} {(effectiveMatches[0] as any).court}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              /* Multiple matches — carousel */
              <>
                <Carousel setApi={setMatchCarouselApi} className="w-full">
                  <CarouselContent>
                    {effectiveMatches.map((match, idx) => {
                      const p0 = isUsingScheduleFallback ? (match as any).participants?.[0] || '' : getParticipantName(match.participants[0]);
                      const p1 = isUsingScheduleFallback ? (match as any).participants?.[1] || '' : getParticipantName(match.participants[1]);
                      const time = isUsingScheduleFallback ? (match as any).startTime : (match as any).time;
                      const court = isUsingScheduleFallback ? (match as any).court : (match as any).court;
                      const isP0User = isUsingScheduleFallback 
                        ? isUserMatchSchedule({ participants: [p0] })
                        : isUserLetter(match.participants[0]);
                      const isP1User = isUsingScheduleFallback
                        ? isUserMatchSchedule({ participants: [p1] })
                        : isUserLetter(match.participants[1]);
                      return (
                        <CarouselItem key={(match as any).id || idx} className="basis-full">
                          <div className="bg-primary/5 rounded-lg border border-primary/10 p-3">
                            <div className="flex items-center justify-between">
                              <div className="text-center flex-1">
                                <p className={`text-xs md:text-sm font-semibold ${isP0User ? 'text-primary' : 'text-foreground'}`}>
                                  {p0}
                                </p>
                              </div>
                              <span className="mx-2 text-xs font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5">VS</span>
                              <div className="text-center flex-1">
                                <p className={`text-xs md:text-sm font-semibold ${isP1User ? 'text-primary' : 'text-foreground'}`}>
                                  {p1}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center justify-center gap-3 mt-2 text-[10px] md:text-xs text-muted-foreground">
                              {time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatTo12h(time)}
                                </span>
                              )}
                              {court && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {t('events.court')} {court}
                                </span>
                              )}
                            </div>
                          </div>
                        </CarouselItem>
                      );
                    })}
                  </CarouselContent>
                </Carousel>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => matchCarouselApi?.scrollPrev()}
                    disabled={matchCurrentSlide === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex gap-1">
                    {effectiveMatches.map((_, idx) => (
                      <button
                        key={idx}
                        className={`h-1.5 rounded-full transition-all ${idx === matchCurrentSlide ? 'w-3 bg-primary' : 'w-1.5 bg-muted-foreground/30'}`}
                        onClick={() => matchCarouselApi?.scrollTo(idx)}
                      />
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => matchCarouselApi?.scrollNext()}
                    disabled={matchCurrentSlide === effectiveMatches.length - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
        {isTournament && tournamentLoading && (
          <div className="p-4">
            <div className="h-24 bg-muted rounded-lg animate-pulse" />
          </div>
        )}
        {isTournament && !tournamentLoading && effectiveMatches.length === 0 && (
          <div className="p-4 text-center">
            <p className="text-sm text-muted-foreground">{t('events.groupsPending')}</p>
          </div>
        )}

        {event.isLeague && !leagueLoading && userMatchForEncounter && (
          <div className="p-3 md:p-4">
            {isTennis && tennisChallenge ? (
              <TennisChallengeCard 
                encounter={tennisChallenge} 
                weekNumber={encounterInfo!.encounterNumber} 
                dateLabel={encounterInfo!.dateRangeLabel} 
              />
            ) : (
              <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-3 md:p-4 border border-primary/10">
                <p className="text-xs font-semibold text-primary mb-2">
                  {isTennis && encounterInfo 
                    ? `${t('events.encounter')} ${encounterInfo.encounterNumber} · ${encounterInfo.dateRangeLabel}`
                    : t('events.nextEncounter')}
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <p className="text-xs md:text-sm font-semibold text-foreground">{userTeam?.player1 || (language === 'es' ? 'Tú' : 'You')}</p>
                    {userTeam?.player2 && <p className="text-[10px] md:text-xs text-muted-foreground">{userTeam.player2}</p>}
                  </div>
                  <div className="mx-3 flex flex-col items-center">
                    <span className="text-xs font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5">VS</span>
                  </div>
                  <div className="text-center flex-1">
                    <p className="text-xs md:text-sm font-semibold text-foreground">{opponentTeam?.player1 || t('events.opponent')}</p>
                    {opponentTeam?.player2 && <p className="text-[10px] md:text-xs text-muted-foreground">{opponentTeam.player2}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-center gap-3 mt-2 text-[10px] md:text-xs text-muted-foreground">
                  {userMatchForEncounter.scheduled_date && (
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" />
                      {parseLocalDate(userMatchForEncounter.scheduled_date).toLocaleDateString(dateLocaleStr, { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                  {userMatchForEncounter.court_number && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {t('events.court')} {userMatchForEncounter.court_number}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {event.isLeague && leagueLoading && (
          <div className="p-4">
            <div className="h-20 bg-muted rounded-lg animate-pulse" />
          </div>
        )}
        {event.isLeague && !leagueLoading && !userMatchForEncounter && (
          <div className="p-3 md:p-4">
            {isTennis && tennisChallenge && encounterInfo ? (
              <TennisChallengeCard 
                encounter={tennisChallenge} 
                weekNumber={encounterInfo.encounterNumber} 
                dateLabel={encounterInfo.dateRangeLabel} 
              />
            ) : isTennis && encounterInfo ? (
              <p className="text-sm text-muted-foreground text-center">
                {t('events.encounter')} {encounterInfo.encounterNumber} · {encounterInfo.dateRangeLabel}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground text-center">{t('events.matchesPending')}</p>
            )}
          </div>
        )}

        <div className="px-4 py-2 bg-muted/30 border-t border-border/50 flex items-center justify-center gap-1.5">
          <span className="text-[10px] md:text-xs text-muted-foreground">{t('events.tapForDetails')}</span>
          <Trophy className="h-3 w-3 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
};

const UpcomingEventPreview = ({ events, onEventClick }: UpcomingEventPreviewProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const { t } = useLanguage();

  const syncHeight = useCallback(() => {
    if (!viewportRef.current) return;
    const idx = api?.selectedScrollSnap() ?? 0;
    const slide = slideRefs.current[idx];
    if (slide) {
      viewportRef.current.style.height = `${slide.scrollHeight + 8}px`;
    }
  }, [api]);

  // Capture the Embla viewport (the overflow-hidden div)
  useEffect(() => {
    if (!api) return;
    const rootNode = api.rootNode() as HTMLDivElement | null;
    if (rootNode) {
      viewportRef.current = rootNode;
      rootNode.style.transition = 'height 0.3s ease';
    }
    setCurrent(api.selectedScrollSnap());
    syncHeight();
    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      syncHeight();
    };
    api.on('select', onSelect);
    return () => { api.off('select', onSelect); };
  }, [api, syncHeight]);

  // ResizeObserver to handle async content loading
  useEffect(() => {
    const observers: ResizeObserver[] = [];
    slideRefs.current.forEach((slide) => {
      if (!slide) return;
      const ro = new ResizeObserver(() => syncHeight());
      ro.observe(slide);
      observers.push(ro);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [events.length, syncHeight]);

  const showCarousel = events.length > 1;

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <div className="h-1 w-1 rounded-full bg-primary" />
        <h2 className="text-sm md:text-base font-semibold text-primary">
          {events.length > 1 ? t('events.yourNextEvents') : t('events.yourNextEvent')}
        </h2>
      </div>

      {showCarousel ? (
        <>
          <Carousel setApi={setApi} opts={{ align: 'start', loop: false }}>
            <CarouselContent className="-ml-3 items-start">
              {events.map((event, idx) => (
                <CarouselItem key={event.id} className="pl-3 basis-full h-auto">
                  <div ref={(el) => { slideRefs.current[idx] = el; }}>
                    <EventSlide event={event} onEventClick={onEventClick} />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {/* Pagination dots */}
          <div className="flex justify-center gap-1.5 pt-1">
            {events.map((_, idx) => (
              <button
                key={idx}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === current ? 'w-4 bg-[#3D6B4F]' : 'w-1.5 bg-[#3D6B4F]/30'
                }`}
                onClick={() => api?.scrollTo(idx)}
              />
            ))}
          </div>
        </>
      ) : (
        <EventSlide event={events[0]} onEventClick={onEventClick} />
      )}
    </div>
  );
};

export default UpcomingEventPreview;
